<script setup lang="ts">

import {useGeoMap} from "~/composables/useGeoMaps";
import type {PortfolioFiltersFormType, PortfolioType} from "~/types/Portfolio";
import {endpoints} from "~/constants/endpoints";
import noResults from '~/assets/img/no_results.svg'
import {createReusableTemplate} from "@vueuse/core";

const route = useRoute();
const router = useRouter()
const globalStore = useGlobalStore()
const config = useRuntimeConfig()

const AsideEnum = {
    MASTERS: 'masters',
    FILTERS: 'filters',
} as const;

const activeAside = ref<typeof AsideEnum[keyof typeof AsideEnum]>(AsideEnum.MASTERS);
const filtersModalIsOpen = ref(false)
const specificationId = computed(() => {
    if(globalStore.specifications.find(item => item.documentId === route.params.id)){
        return route.params.id
    }
    const foundSpecification = globalStore.specifications.find(item => item.slug === route.params.slug)?.documentId
    if (!foundSpecification) {
        throw createError({
            statusCode: 404,
            message: 'Specification not found'
        })
    }

    return foundSpecification
})
// Используем композаблы
const {
    isLoaded,
    //filterForm,
    mastersList,
    total,
    filtersList,
    currentSpecification,
    parentSpecification,
    specificationChildList,
    //cleanedFilterForm,
    getFilters,
} = usePortfolio(specificationId.value as string);

const {
    map,
    zoom,
    center,
    userLocation,
    bounds,
    locateUser,
    setupMarkers,
} = useGeoMap();

const filterForm = ref<PortfolioFiltersFormType>({
    specificationId: [specificationId.value as string],
    city: null,
    educationType: null,
    employmentType: null,
    isRemote: null,
    minLat: null,
    maxLat: null,
    minLng: null,
    maxLng: null,
    priceFrom: null,
    priceTo: null,
    pageSize: 15,
    page: 1,
    reviewsCount: null,
    experience: null
});

await getFilters();

// Обработчик готовности карты
async function onMapReady() {
    locateUser();
    await setupMarkers(mastersList.value);
}

function onLocationFound(location: any) {
    userLocation.value = location.latlng
    center.value = location.latlng
    const {filterBounds} = useBoundsCalculator(location.latlng)
    if(filterBounds){
        bounds.value = filterBounds.value
    }
    map.value?.leafletObject?.panTo(location.latlng)
    console.log('onLocationFound')
    getMasters()
}

const cleanedFilterForm = computed(() => {
    const form = { ...filterForm.value }

    return Object.entries(form).reduce((acc, [key, value]) => {
        // Пропускаем null, undefined и пустые массивы
        if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
            return acc
        }

        if (Array.isArray(value)) {
            acc[key] = value.join(',')
            return acc
        }

        acc[key] = value
        return acc
    }, {})
})

async function getMasters() {
    if (!cleanedFilterForm.value?.specificationId) return []
    await nextTick()
    let query = {
        ...cleanedFilterForm.value,
        pageSize: 999999,
    }
    
    if(bounds.value){
        query = {
            ...query,
            ...bounds.value
        }
    }
    const response = await fetchData<PortfolioType[]>(endpoints.portfolio.search, {
        method: 'GET',
        query: query
    }).then(({data, meta}) => {
        return data
    }).catch(e => {
        console.error(e)
    })
    mastersList.value = response?.filter(portfolio => !!portfolio.geo && !!portfolio.user) || []

    await setupMarkers(mastersList.value)
}

async function submitFilters(localFilters: any){
    filterForm.value = localFilters
    await getMasters();
    await setupMarkers(mastersList.value);
    activeAside.value = AsideEnum.MASTERS
    filtersModalIsOpen.value = false
}


useSeoMeta({
    robots: 'index, follow',
    title: currentSpecification.value?.name,
    description: () => currentSpecification.value?.description,
    ogImage: currentSpecification.value?.thumbnail?.url,
    ogImageType: 'image/jpeg',
    ogType: 'website',
    ogUrl: () => config.public.frontUrl + route.path,
    ogTitle: () => currentSpecification.value?.name,
    ogDescription: () => currentSpecification.value?.description,
    twitterTitle: () => currentSpecification.value?.name,
    twitterDescription: () => currentSpecification.value?.description,
    twitterImage: currentSpecification.value?.thumbnail?.url,
    twitterImageType: 'image/jpeg',
})

</script>

<template>
    <div class="">
    <div class="mobile_header md:hidden grid grid-cols-6 items-center mb-4">
        <div class="col-span-1">
            <UiButton @click.prevent="router.back()" variant="ghost" class="p-0">
                <Icon name="ri:arrow-left-line" size="24px" />
            </UiButton>
        </div>
        <div class="col-span-4">
            <p class="text-center font-bold uppercase text-[13px] tracking-widest leading-none">{{ $t('Specialists nearby') }}</p>
        </div>
        <div class="col-span-1">
        </div>
    </div>
    <div class="grid gap-4 lg:gap-12 lg:grid-cols-12">
        <div v-if="activeAside === AsideEnum.MASTERS" class="aside order-2 lg:order-1 lg:col-span-4">
            <div class="hidden lg:block page_title mb-8">
                <div class="flex items-center gap-6">
                    <UiButton @click.prevent="router.back()" class="p-3 h-auto text-black" variant="lighter">
                        <Icon name="ri:arrow-left-line" size="24px"/>
                    </UiButton>
                    <p class="font-semibold text-xl">{{ currentSpecification?.name }}</p>
                </div>
            </div>
            
            <div class="mb-4">
                <UiButton class="hidden lg:flex p-3 h-auto w-full rounded-lg text-dark" variant="lighter"
                          @click.prevent="activeAside = AsideEnum.FILTERS">
                    <Icon name="ri:equalizer-line" size="24px"/>
                    <span class="text-sm font-medium ml-2">{{ $t('Filters') }}</span>
                </UiButton>
                
                <UiDialog v-model:open="filtersModalIsOpen">
                    <UiDialogTrigger as-child>
                        <UiButton class="flex lg:hidden p-3 h-auto w-full rounded-lg text-dark" variant="lighter">
                            <Icon name="ri:equalizer-line" size="24px"/>
                            <span class="text-sm font-medium ml-2">{{ $t('Filters') }}</span>
                        </UiButton>
                    </UiDialogTrigger>
                    <UiDialogContent class="!p-4 gap-0 ys-modal-dialog">
                        <UiDialogClose class="absolute top-4 ltr:right-4 rtl:left-4">
                            <Icon name="ri:close-line" size="20px"
                                  class="text-gray-500 translate-all hover:text-gray-700" />
                        </UiDialogClose>
                        <PagesCatalogGeoCatalogFilters
                                :filters-list="filtersList"
                                :masters-count="mastersList.length"
                                :parent-specification="parentSpecification"
                                :specificationChildList="specificationChildList"
                                :filterForm="filterForm"
                                @submit="submitFilters"
                                @back="activeAside = AsideEnum.MASTERS"
                                @closeFiltersModal="filtersModalIsOpen = false"
                        />
                    </UiDialogContent>
                </UiDialog>
                
                <div class="masters_count text-center text-gray-400 text-sm mt-2">
                    {{ mastersList.length }} {{ $t('specialists nearby') }}
                </div>
            </div>

            <template v-if="isLoaded">
                <UiScrollArea v-if="mastersList.length" class="h-[600px]">
                    <div class="grid gap-7">
                        <PagesCatalogGeoMasterCard
                                v-for="item in mastersList"
                                :current_specification="currentSpecification.documentId"
                                :item="item"
                                :parent_specification="parentSpecification?.documentId"
                        />
                    </div>
                </UiScrollArea>

                <div v-else class="">
                    <div class="p-4 rounded-md font-bold text-gray-500 text-xs text-center bg-gray-100">
                        <p>{{$t('Unfortunately, there are no masters on the map according to the selected filters. Try to change filtering parameters and repeat the search.')}}</p>
                    </div>
                    <img src="" alt="">
                    <noResults class="mt-8 max-w-[270px] mx-auto" />
                </div>
            </template>
        </div>
        
        <div v-if="activeAside === AsideEnum.FILTERS" class="aside order-2 lg:order-1 lg:col-span-4">
            <PagesCatalogGeoCatalogFilters
                    :filters-list="filtersList"
                    :masters-count="mastersList.length"
                    :parent-specification="parentSpecification"
                    :specificationChildList="specificationChildList"
                    :filterForm="filterForm"
                    @submit="submitFilters"
                    @back="activeAside = AsideEnum.MASTERS"
                    @closeFiltersModal="filtersModalIsOpen = false"
            />
        </div>

        <div class="order-1 lg:order-2 col-span-1 lg:col-span-8">
            <client-only>
                <LMap
                        ref="map"
                        :center="center"
                        :options="{
                            attributionControl: false,
                            zoomControl: false,
                        }"
                        :use-global-leaflet="true"
                        :zoom="zoom"
                        class="rounded-2xl min-h-[400px] lg:min-h-[800px] z-0"
                        @locationfound="onLocationFound"
                        @ready="onMapReady"
                >
                    <LTileLayer
                            attribution="&amp;copy; <a href=&quot;https://www.openstreetmap.org/&quot;>OpenStreetMap</a> contributors"
                            layer-type="base"
                            name="OpenStreetMap"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LMarker v-if="userLocation" :lat-lng="userLocation" color="red">
                        <LIcon :icon-size="[60, 60]"
                               class-name="relative overflow-visible flex items-center justify-center">
                            <div
                                    class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-primary-400"></div>
                            <div
                                    class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white border-[6px] border-primary-400"></div>
                        </LIcon>
                    </LMarker>

<!--                    <template v-for="portfolio in mastersList">
                        <LMarker v-if="portfolio.geo" :lat-lng="[portfolio.geo.lat, portfolio.geo.lng]" color="red">
                            <LIcon :icon-size="[48, 48]" class-name="">
                                <NuxtLink :to="{name: 'portfolio', params: {id: portfolio.documentId}, query: {mode: 'geo'}}">
                                    <CustomUiUserAvatar
                                            :name="portfolio.user.username"
                                            :src="portfolio.user.avatar?.formats?.small?.url"
                                            class="border-2 border-white shadow"
                                            size="xs"/>
                                </NuxtLink>
                            </LIcon>
                        </LMarker>
                    </template>-->


                    <LControlZoom position="bottomright"/>
                    <LControl position="bottomright">
                        <button
                                class="leaflet_location_btn w-12 h-12 mb-4 rounded-full leading-[48px] bg-white hover:bg-gray-100 focus:outline-none"
                                @click="locateUser"
                        >
                            <Icon
                                    :class="{
                                        'text-gray-400': !userLocation,
                                        'text-primary-400': !!userLocation,
                                    }"
                                    name="ri:send-plane-fill"
                                    size="18px"/>
                        </button>
                    </LControl>
                    <LControl position="topleft">

                        <UiTabs
                                @update:modelValue="navigateTo({name: 'master_category', params: {slug: currentSpecification?.slug}, query: {mode: mode}})"
                                default-value="geo"
                                class="bg-white rounded-xl"
                        >
                            <UiTabsList>
                                <UiTabsTrigger value="geo" class="rounded-xl py-3 data-[state=active]:bg-primary-500 data-[state=active]:text-white">
                                    <Icon name="ri:map-2-line" size="24" />
                                </UiTabsTrigger>
                                <UiTabsTrigger value="list" class="rounded-xl py-3 data-[state=active]:bg-primary-500 data-[state=active]:text-white" >
                                    <Icon name="ri:list-check-2" size="24" />
                                </UiTabsTrigger>
                            </UiTabsList>
                        </UiTabs>
                    </LControl>
                </LMap>
            </client-only>
        </div>
    </div>
    </div>
</template>

<style lang="scss">

</style>